// pages/SecretStaffAdmin.tsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Box,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
  Work as WorkIcon
} from '@mui/icons-material';
import axiosInstance from '../utils/axiosConfig';

interface StaffMember {
  _id: string;
  name: string;
  email: string;
  employmentType: 'full-time' | 'part-time' | 'contractor';
  startDate: string;
  leaveEntitlement?: {
    annualLeaveTotal: number;
    annualLeaveUsed: number;
    annualLeaveYear: number;
    timeInLieuHours: number;
    timeInLieuUsed: number;
    lastModified: string;
  };
}

interface EditData {
  employmentType: string;
  startDate: string;
  annualLeaveTotal: number;
}

const SecretStaffAdmin: React.FC = () => {
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState<StaffMember | null>(null);
  const [editData, setEditData] = useState<EditData>({
    employmentType: 'full-time',
    startDate: '',
    annualLeaveTotal: 20
  });
  const [saving, setSaving] = useState(false);

const [christmasLeaveDialog, setChristmasLeaveDialog] = useState(false);
const [christmasYear, setChristmasYear] = useState(new Date().getFullYear());
const [christmasDates, setChristmasDates] = useState<string[]>([]);
const [applyingChristmasLeave, setApplyingChristmasLeave] = useState(false);
const [lastChristmasApplication, setLastChristmasApplication] = useState<{
  year: number;
  date: string;
  staffCount: number;
  datesCount: number;
} | null>(null);
const [showPreviousApplication, setShowPreviousApplication] = useState(false);

// Christmas closure dates based on IMC policy (from screenshot)
const getDefaultChristmasDates = (year: number): string[] => {
  return [
    `${year}-12-23`, // Dec 23
    `${year}-12-24`, // Dec 24
    `${year}-12-28`, // Dec 28
    `${year}-12-29`, // Dec 29
    `${year}-12-30`, // Dec 30
    `${year}-12-31`, // Dec 31
    `${year + 1}-01-02`  // Jan 2
  ];
};

// Apply Christmas leave to all staff
const applyChristmasLeaveToAllStaff = async () => {
  try {
    setApplyingChristmasLeave(true);
    setError(null);

    const results = [];
    
    for (const staffMember of staff) {
      try {
        // Create rota entries for company leave
        const rotaEntries = christmasDates.map(date => ({
          personnelId: staffMember._id,
          date: date,
          status: 'company-leave'
        }));

        await axiosInstance.post('/api/rota/bulk', {
          entries: rotaEntries
        });

        results.push({ name: staffMember.name, success: true });
      } catch (err) {
        console.error(`Failed to apply Christmas leave for ${staffMember.name}:`, err);
        results.push({ 
          name: staffMember.name, 
          success: false, 
          error: (err as any).response?.data?.message 
        });
      }
    }

    const successCount = results.filter(r => r.success).length;
    const failCount = results.filter(r => !r.success).length;

    setSuccess(
      `Christmas leave applied! ✅ ${successCount} successful, ❌ ${failCount} failed`
    );
    
    // Store the application details
    setLastChristmasApplication({
      year: christmasYear,
      date: new Date().toISOString(),
      staffCount: successCount,
      datesCount: christmasDates.length
    });
    
    setChristmasLeaveDialog(false);
    
  } catch (err: any) {
    setError('Failed to apply Christmas leave: ' + err.message);
  } finally {
    setApplyingChristmasLeave(false);
  }
};


  // Fetch staff data from your API
  const fetchStaff = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use your existing API endpoint
      const response = await axiosInstance.get('/api/personnel/staff/leave-balances');
      setStaff(response.data);
    } catch (err: any) {
      console.error('Error fetching staff:', err);
      setError(err.response?.data?.message || 'Failed to load staff data');
    } finally {
      setLoading(false);
    }
  };

  // Update staff member using your existing API
  const updateStaffMember = async (staffId: string, updates: EditData) => {
    try {
      setSaving(true);
      setError(null);
      
      // Update employment details
      await axiosInstance.patch(`/api/personnel/${staffId}`, {
        employmentType: updates.employmentType,
        startDate: updates.startDate
      });
      
      // Update annual leave entitlement
      await axiosInstance.patch(`/api/personnel/${staffId}/leave/adjust-annual`, {
        annualLeaveTotal: updates.annualLeaveTotal,
        reason: 'Secret admin adjustment via super admin panel'
      });

      // Refresh data
      await fetchStaff();
      
      setSuccess(`Successfully updated ${editingStaff?.name}`);
      setEditDialogOpen(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
      
    } catch (err: any) {
      console.error('Error updating staff:', err);
      setError(err.response?.data?.message || 'Failed to update staff member');
    } finally {
      setSaving(false);
    }
  };

  const handleEditClick = (staffMember: StaffMember) => {
    setEditingStaff(staffMember);
    setEditData({
      employmentType: staffMember.employmentType,
      startDate: staffMember.startDate.split('T')[0], // Format for date input
      annualLeaveTotal: staffMember.leaveEntitlement?.annualLeaveTotal || 20
    });
    setEditDialogOpen(true);
  };

  const handleSave = () => {
    if (editingStaff) {
      updateStaffMember(editingStaff._id, editData);
    }
  };

  // Initialize default dates when dialog opens
const handleChristmasDialogOpen = () => {
  setChristmasDates(getDefaultChristmasDates(christmasYear));
  setChristmasLeaveDialog(true);
};


  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-IE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getEmploymentTypeColor = (type: string) => {
    switch (type) {
      case 'full-time': return 'success';
      case 'part-time': return 'warning';
      case 'contractor': return 'info';
      default: return 'default';
    }
  };

  const calculateRemainingLeave = (entitlement: StaffMember['leaveEntitlement']) => {
    if (!entitlement) return 0;
    return entitlement.annualLeaveTotal - entitlement.annualLeaveUsed;
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading staff data...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Secret Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'error.main' }}>
          STAFF ADMIN
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Employment & Leave Management
        </Typography>
      </Box>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PersonIcon color="primary" />
                <Box>
                  <Typography variant="h4">{staff.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Staff Members
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <WorkIcon color="success" />
                <Box>
                  <Typography variant="h4">
                    {staff.filter(s => s.employmentType === 'full-time').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Full-time Staff
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <WarningIcon color="warning" />
                <Box>
                  <Typography variant="h4">
                    {staff.filter(s => s.leaveEntitlement && s.leaveEntitlement.timeInLieuHours > 16).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    TIL Over 16h
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Christmas Leave Management */}
{/* Christmas Leave Management */}
<Card sx={{ mb: 4, border: '2px solid', borderColor: 'error.main' }}>
  <CardContent>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box>
        <Typography variant="h6" color="error.main" gutterBottom>
          🎄 Christmas Company Leave
        </Typography>
        {lastChristmasApplication ? (
          <Box>
            <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold' }}>
              ✅ Applied for {lastChristmasApplication.year}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Last applied: {new Date(lastChristmasApplication.date).toLocaleDateString()} 
              • {lastChristmasApplication.staffCount} staff members
              • {lastChristmasApplication.datesCount} closure dates
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Apply company closure dates to all staff members. This will add "COMP" 
            entries to everyone's rota for the Christmas/New Year period.
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Button
          variant="contained"
          color="error"
          onClick={handleChristmasDialogOpen}
          startIcon={<WarningIcon />}
          sx={{ minWidth: '200px' }}
        >
          {lastChristmasApplication ? 'Update Christmas Leave' : 'Setup Christmas Leave'}
        </Button>
        {lastChristmasApplication && (
          <Button
            variant="outlined"
            color="error"
            onClick={() => setShowPreviousApplication(true)}
            sx={{ minWidth: '200px', fontSize: '0.75rem' }}
          >
            View Applied Dates
          </Button>
        )}
      </Box>
    </Box>
  </CardContent>
</Card>

{/* Christmas Leave Dialog */}
<Dialog 
  open={christmasLeaveDialog} 
  onClose={() => setChristmasLeaveDialog(false)} 
  maxWidth="md" 
  fullWidth
>
  <DialogTitle sx={{ bgcolor: 'error.main', color: 'white' }}>
    🎄 Apply Christmas Company Leave
  </DialogTitle>
  <DialogContent sx={{ pt: 3 }}>
    <Alert severity="warning" sx={{ mb: 3 }}>
      <Typography variant="body2" fontWeight="bold" gutterBottom>
        This will apply company leave to ALL {staff.length} staff members!
      </Typography>
      <Typography variant="caption">
        Staff will see "COMP" entries in their rota calendar for these dates.
        This does NOT automatically deduct from annual leave - that's handled separately.
      </Typography>
    </Alert>

    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="number"
          label="Year"
          value={christmasYear}
          onChange={(e) => {
            const year = parseInt(e.target.value);
            setChristmasYear(year);
            setChristmasDates(getDefaultChristmasDates(year));
          }}
          inputProps={{ min: 2024, max: 2030 }}
        />
      </Grid>
    </Grid>

    <Typography variant="subtitle2" sx={{ mt: 3, mb: 2 }}>
      Company Closure Dates:
    </Typography>
    
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
      {christmasDates.map((date, index) => (
        <Chip
          key={index}
          label={new Date(date + 'T12:00:00').toLocaleDateString('en-IE', {
            month: 'short',
            day: 'numeric'
          })}
          onDelete={() => {
            setChristmasDates(prev => prev.filter((_, i) => i !== index));
          }}
          color="error"
          variant="outlined"
        />
      ))}
    </Box>

    <TextField
      fullWidth
      type="date"
      label="Add Additional Date"
      onChange={(e) => {
        if (e.target.value && !christmasDates.includes(e.target.value)) {
          setChristmasDates(prev => [...prev, e.target.value].sort());
        }
      }}
      InputLabelProps={{ shrink: true }}
    />

    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
      💡 Note: Dec 25 & 26 are bank holidays. These are the actual IMC closure dates.
    </Typography>
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setChristmasLeaveDialog(false)}>
      Cancel
    </Button>
    <Button
      onClick={applyChristmasLeaveToAllStaff}
      variant="contained"
      color="error"
      disabled={applyingChristmasLeave || christmasDates.length === 0}
      startIcon={applyingChristmasLeave ? <CircularProgress size={20} /> : <WarningIcon />}
    >
      {applyingChristmasLeave 
        ? 'Applying...' 
        : `Apply to ${staff.length} Staff Members`
      }
    </Button>
  </DialogActions>
</Dialog>

{/* Previous Application Details Dialog */}
<Dialog 
  open={showPreviousApplication} 
  onClose={() => setShowPreviousApplication(false)} 
  maxWidth="sm" 
  fullWidth
>
  <DialogTitle>Previously Applied Christmas Leave</DialogTitle>
  <DialogContent>
    {lastChristmasApplication && (
      <Box>
        <Typography variant="body1" gutterBottom>
          <strong>Year:</strong> {lastChristmasApplication.year}
        </Typography>
        <Typography variant="body1" gutterBottom>
          <strong>Applied:</strong> {new Date(lastChristmasApplication.date).toLocaleString()}
        </Typography>
        <Typography variant="body1" gutterBottom>
          <strong>Staff Count:</strong> {lastChristmasApplication.staffCount} members
        </Typography>
        <Typography variant="body1" gutterBottom>
          <strong>Dates Applied:</strong> {lastChristmasApplication.datesCount} closure dates
        </Typography>
      </Box>
    )}
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setShowPreviousApplication(false)}>Close</Button>
  </DialogActions>
</Dialog>

      {/* Staff Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Employment Type</strong></TableCell>
              <TableCell><strong>Start Date</strong></TableCell>
              <TableCell><strong>Annual Leave</strong></TableCell>
              <TableCell><strong>TIL Hours</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {staff.map((member) => (
              <TableRow key={member._id}>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {member.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {member.email}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={member.employmentType.replace('-', ' ').toUpperCase()}
                    color={getEmploymentTypeColor(member.employmentType)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(member.startDate)}
                  </Typography>
                </TableCell>
                <TableCell>
                  {member.leaveEntitlement ? (
                    <Box>
                      <Typography variant="body2">
                        {calculateRemainingLeave(member.leaveEntitlement)} / {member.leaveEntitlement.annualLeaveTotal} days
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Used: {member.leaveEntitlement.annualLeaveUsed}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No entitlement
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {member.leaveEntitlement ? (
                    <Box>
                      <Typography variant="body2">
                        {member.leaveEntitlement.timeInLieuHours - member.leaveEntitlement.timeInLieuUsed}h available
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total: {member.leaveEntitlement.timeInLieuHours}h
                      </Typography>
                      {member.leaveEntitlement.timeInLieuHours > 16 && (
                        <Chip label="⚠️ Over 16h" size="small" color="warning" sx={{ ml: 1 }} />
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No TIL
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title="Edit Employment & Leave">
                    <IconButton
                      onClick={() => handleEditClick(member)}
                      size="small"
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {staff.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No staff members found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Make sure personnel have the "Staff" role assigned
          </Typography>
        </Box>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Staff Member: {editingStaff?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Employment Type</InputLabel>
                <Select
                  value={editData.employmentType}
                  onChange={(e) => setEditData({ ...editData, employmentType: e.target.value })}
                  label="Employment Type"
                >
                  <MenuItem value="full-time">Full-time</MenuItem>
                  <MenuItem value="part-time">Part-time</MenuItem>
                  <MenuItem value="contractor">Contractor</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                InputLabelProps={{ shrink: true }}
                value={editData.startDate}
                onChange={(e) => setEditData({ ...editData, startDate: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Annual Leave Entitlement (Days)"
                value={editData.annualLeaveTotal}
                onChange={(e) => setEditData({ ...editData, annualLeaveTotal: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 0, max: 50 }}
                helperText="Enter 0 for contractors, 20 for full-time staff"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SecretStaffAdmin;